import { Component, OnDestroy, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';
import { Subscription } from 'rxjs';
import { AnalyticsService } from './services/analytics.service';
import { ConsentService } from './services/consent.service';
import { ConsentUiService } from './services/consent-ui.service';
import { ThemeService } from './services/theme.service';

const ADVANCED_OPTIONS_STORAGE_KEY = 'noisy_user_advanced_options_enabled';
const ADVANCED_OPTIONS_EVENT = 'noisy_user_advanced_options_changed';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  showConsentModal = false;
  showConsentCustomization = false;
  consentAnalytics = false;
  consentAds = false;
  bigRedThemeActive = false;
  advancedOptionsEnabled = false;

  private subscriptions = new Subscription();

  constructor(
    private consentService: ConsentService,
    private consentUi: ConsentUiService,
    private analyticsService: AnalyticsService,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.configureStatusBar();
    if (!this.consentService.hasStoredConsent()) {
      this.consentService.acceptAll();
    }
    this.syncConsentState();
    this.analyticsService.start();

    this.themeService.enableBigRedButtonTheme();
    this.bigRedThemeActive = this.themeService.isBigRedButtonThemeActive();
    this.advancedOptionsEnabled = this.loadAdvancedOptionsState();

    this.subscriptions.add(
      this.consentUi.open$.subscribe(() => {
        this.openConsentPreferences(true);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openConsentPreferences(forceCustomize = false): void {
    this.syncConsentState();
    this.showConsentCustomization = forceCustomize || this.consentService.hasStoredConsent();
    this.showConsentModal = true;
  }

  closeConsentModal(): void {
    this.showConsentModal = false;
    this.showConsentCustomization = false;
  }

  acceptAllConsent(): void {
    this.consentService.acceptAll();
    this.closeConsentModal();
  }

  rejectAllConsent(): void {
    this.consentService.rejectAll();
    this.closeConsentModal();
  }

  saveCustomConsent(): void {
    this.consentService.saveCustom(this.consentAnalytics, this.consentAds);
    this.closeConsentModal();
  }

  private syncConsentState(): void {
    const state = this.consentService.getState();
    this.consentAnalytics = state?.analytics ?? false;
    this.consentAds = state?.ads ?? false;
  }

  toggleBigRedTheme(event: CustomEvent<{ checked: boolean }>): void {
    this.setBigRedTheme(event.detail.checked);
  }

  toggleAdvancedOptions(event: CustomEvent<{ checked: boolean }>): void {
    this.setAdvancedOptions(event.detail.checked);
  }

  private setBigRedTheme(enabled: boolean): void {
    this.bigRedThemeActive = enabled;
    if (enabled) {
      this.themeService.enableBigRedButtonTheme();
    } else {
      this.themeService.disableBigRedButtonTheme();
    }
  }

  private setAdvancedOptions(enabled: boolean): void {
    this.advancedOptionsEnabled = enabled;
    localStorage.setItem(ADVANCED_OPTIONS_STORAGE_KEY, String(enabled));
    window.dispatchEvent(new CustomEvent(ADVANCED_OPTIONS_EVENT, { detail: { enabled } }));
  }

  private loadAdvancedOptionsState(): boolean {
    const stored = localStorage.getItem(ADVANCED_OPTIONS_STORAGE_KEY);
    return stored === 'true';
  }

  private configureStatusBar(): void {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    StatusBar.setOverlaysWebView({ overlay: false })
      .catch((error) => {
        console.warn('[StatusBar] Failed to disable overlay', error);
      });
  }
}
